import React from 'react';
import { useNavigate } from 'react-router-dom';

// Basic styling - can be moved to a dedicated SCSS file later
const styles = {
  container: {
    maxWidth: '800px',
    margin: '2rem auto',
    padding: '2rem',
    fontFamily: 'sans-serif',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  header: {
    borderBottom: '1px solid #eee',
    paddingBottom: '1rem',
    marginBottom: '1.5rem',
  },
  h1: {
    fontSize: '2rem',
    color: '#333',
  },
  h2: {
    fontSize: '1.5rem',
    color: '#555',
    marginTop: '2rem',
    marginBottom: '1rem',
  },
  paragraph: {
    lineHeight: '1.6',
    color: '#444',
    marginBottom: '1rem',
  },
  list: {
    listStyle: 'none',
    paddingLeft: '0',
  },
  listItem: {
    marginBottom: '1.5rem',
    paddingLeft: '1.5rem',
    borderLeft: '3px solid #007bff', // Blue accent for the principle
  },
  listItemPrimary: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '0.25rem',
  },
  listItemSecondary: {
    fontSize: '0.95rem',
    color: '#555',
    lineHeight: '1.5',
    marginBottom: '0.75rem', // Add some space before Pulc's action
  },
  pulcAction: { // Style for "What Pulc does" section
    marginTop: '0.5rem',
    padding: '0.75rem',
    backgroundColor: '#e9f5ff', // Light blue background
    borderLeft: '3px solid #0056b3', // Darker blue accent
    borderRadius: '4px',
  },
  placeholderText: { // Style for the placeholder text
    fontStyle: 'italic',
    color: '#777',
    fontSize: '0.9rem',
    margin: '0.25rem 0 0 0',
  },
  backButton: { // Style for the back button
    display: 'inline-block',
    padding: '0.5rem 1rem',
    margin: '0 0 1rem 0',
    fontSize: '0.9rem',
    color: '#007bff',
    backgroundColor: '#transparent',
    border: '1px solid #007bff',
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
  },
};

const TrustworthyAI = () => {
  const placeholder = "[Please add details here about how Pulc addresses this principle. For example, describe specific features, development practices, or user controls.]";
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      <button onClick={() => navigate(-1)} style={styles.backButton}>
        &larr; Back
      </button>

      <header style={styles.header}>
        <h1 style={styles.h1}>Our Commitment to Trustworthy AI</h1>
      </header>

      <p style={styles.paragraph}>
      We are committed to building Trustworthy AI in education; AI that is human-centric. We
want Pulc to be a leader in both cutting-edge and ethical AI technology. To make this
possible, we have conceived Pulc around the European Commission’s Ethics Guidelines for
Trustworthy AI. You can read more about these <a href="https://www.europarl.europa.eu/cmsdata/196377/AI%20HLEG_Ethics%20Guidelines%20for%20Trustworthy%20AI.pdf" target="_blank" rel="noopener noreferrer">here</a>.
      </p>

      <h2 style={styles.h2}>The 7 Key Requirements for Trustworthy AI</h2>
      <ul style={styles.list}>
        <li style={styles.listItem}>
          <div style={styles.listItemPrimary}>1. Human agency and oversight</div>
          <div style={styles.listItemSecondary}>AI systems should empower human beings, allowing them to make informed decisions and fostering their fundamental rights. At the same time, proper oversight mechanisms need to be ensured, which can be achieved through human-in-the-loop, human-on-the-loop, and human-in-command approaches.</div>
          <div style={styles.pulcAction}>
            <strong>What Pulc does:</strong>
            <p style={styles.placeholderText}>{"Pulc offers teachers complete agency and oversight over feedback that their students receive. Teachers can edit, delete, approve or reject any comments made by the AI. This ensures oversight and human agency within the classroom. Pulc does not replace students’ writing, never telling them what to write, but actively encourages them to reflect on their answers, ensuring that students remain in control of their work from submission to revision. Across the website, we emphasise that you should always trust your teacher’s feedback over Pulc and that any grades provided are estimates which should never be prioritised over human feedback."}</p>
          </div>
        </li>
        <li style={styles.listItem}>
          <div style={styles.listItemPrimary}>2. Technical Robustness and safety</div>
          <div style={styles.listItemSecondary}>AI systems need to be resilient and secure. They need to be safe, ensuring a fall back plan in case something goes wrong, as well as being accurate, reliable and reproducible. That is the only way to ensure that also unintentional harm can be minimized and prevented.</div>
          <div style={styles.pulcAction}>
            <strong>What Pulc does:</strong>
            <p style={styles.placeholderText}>{"Pulc’s essay-grading material is modelled specifically on past exam answers, exam questions and marking criteria. This ensures that the model generates feedback aligned with official standards. We are also committed to robust security online so that your data and activity is fully protected. For this, we follow the latest security protocols and we are constantly reviewing these."}</p>
          </div>
        </li>
        <li style={styles.listItem}>
          <div style={styles.listItemPrimary}>3. Privacy and data governance</div>
          <div style={styles.listItemSecondary}>Besides ensuring full respect for privacy and data protection, adequate data governance mechanisms must also be ensured, taking into account the quality and integrity of the data, and ensuring legitimised access to data.</div>
          <div style={styles.pulcAction}>
            <strong>What Pulc does:</strong>
            <p style={styles.placeholderText}>{"We are committed to ensuring you remain in complete control of your data. Students upload their own answers and can decide when and whether to send their essays to their teachers and remain in full control of this process. Pulc will never sell your personal data, share it with a third party for advertising or reveal any personal or sensitive information and your essays will not be shared publicly. Your data is fully anonymised and stored securely. You always have the right to withdraw your data in full at any stage and you can do so by just emailing us: hello@pulc.ie. We are fully GDPR compliant. For more information, please take a look at our privacy policy: https://www.pulc.ai/privacy"}</p>
          </div>
        </li>
        <li style={styles.listItem}>
          <div style={styles.listItemPrimary}>4. Transparency</div>
          <div style={styles.listItemSecondary}>The data, system and AI business models should be transparent. Traceability mechanisms can help achieving this. Moreover, AI systems and their decisions should be explained in a manner adapted to the stakeholder concerned. Humans need to be aware that they are interacting with an AI system, and must be informed of the system's capabilities and limitations.</div>
          <div style={styles.pulcAction}>
            <strong>What Pulc does:</strong>
            <p style={styles.placeholderText}>{"AI is complex and technical, but our aim is to make AI in education understandable and available to everyone. For that reason, we try to describe all of our processes in plain and clear language. When providing a grade and feedback, we include a practical description of the marking scheme at the top of the page, colour-coded; this means that students and teachers can understand what Pulc’s comments mean and where they come from. As part of using Pulc, we may need to use your essays to improve our model and particularly to ensure that we are giving a consistent grade. When that happens, your data is fully anonymised and stored securely. You always have the right to withdraw your data in full and you can do so just by emailing us at: hello@pulc.ie. This is publicly displayed on the front page of our website, so you know exactly how your data is used."}</p>
          </div>
        </li>
        <li style={styles.listItem}>
          <div style={styles.listItemPrimary}>5. Diversity, non-discrimination and fairness</div>
          <div style={styles.listItemSecondary}>Unfair bias must be avoided, as it could could have multiple negative implications, from the marginalization of vulnerable groups, to the exacerbation of prejudice and discrimination. Fostering diversity, AI systems should be accessible to all, regardless of any disability, and involve relevant stakeholders throughout their entire life circle.</div>
          <div style={styles.pulcAction}>
            <strong>What Pulc does:</strong>
            <p style={styles.placeholderText}>{"A key aim of Pulc is to increase education equality through AI. We think AI represents a unique opportunity to achieve this and that we need to actively shape AI to make this possible. It is for this reason that Pulc is available free to educators. We also actively review Pulc’s feedback and data in our training model to ensure that Pulc does not discriminate against perspectives emerging from different backgrounds or experiences."}</p>
          </div>
        </li>
        <li style={styles.listItem}>
          <div style={styles.listItemPrimary}>6. Societal and environmental well-being</div>
          <div style={styles.listItemSecondary}>AI systems should benefit all human beings, including future generations. It must hence be ensured that they are sustainable and environmentally friendly. Moreover, they should take into account the environment, including other living beings, and their social and societal impact should be carefully considered.</div>
          <div style={styles.pulcAction}>
            <strong>What Pulc does:</strong>
            <p style={styles.placeholderText}>{"In line with our commitment to educational equality outlined above, Pulc believes that AI used responsibly and with a human-centric perspective can make a genuine contribution to societal wellbeing. Alongside environmental wellbeing, this is something we are constantly reflecting on and trying to improve."}</p>
          </div>
        </li>
        <li style={styles.listItem}>
          <div style={styles.listItemPrimary}>7. Accountability</div>
          <div style={styles.listItemSecondary}>Mechanisms should be put in place to ensure responsibility and accountability for AI systems and their outcomes. Auditability, which enables the assessment of algorithms, data and design processes plays a key role therein, especially in critical applications. Moreover, adequate an accessible redress should be ensured.</div>
          <div style={styles.pulcAction}>
            <strong>What Pulc does:</strong>
            <p style={styles.placeholderText}>{"We are fully committed to accountability and the principles of fairness that accountability is based on. While we are a small team, we are aiming to work with academics and other important stakeholders to ensure comprehensive governance. When established, this will ensure that our decisions, methodology and use of your data are auditable."}</p>
          </div>
        </li>
      </ul>

      <p style={{ ...styles.paragraph, marginTop: '2rem' }}>
        At Pulc, we are dedicated to upholding these principles throughout the lifecycle of our AI systems, from design and development to deployment and monitoring. We believe that by doing so, we can build AI that is not only innovative but also worthy of trust.
      </p>
    </div>
  );
};

export default TrustworthyAI; 